import { useQueryClient } from '@tanstack/react-query';
import { Cascader, TimePicker } from 'antd';
import Checkbox from 'antd/lib/checkbox/Checkbox';
import { isBefore, sub } from 'date-fns';
import format from 'date-fns/format';
import isToday from 'date-fns/isToday';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { updateDay } from '../../fetchHooks/updateDay';
import { formatWeekdayOnly } from '../../utils/utilFunctions';
import styles from './Day.module.css';
import RegistrationInputDebounced from './RegistrationInputDebounced';
import { type OrdinaryHours, breakOptions, disabledDateTime, formatCurrency, stringToDaysJsFormat } from './dayUtils';

export type DayType = {
  lunchTime: string;
  isWorkingDay: boolean;
  overtimeHours: number;
  overtimeType: string;
  hourRate: number;
  id: number;
  userId: number;
  description: string;
  day: number;
  month: number;
  project: string;
  year: number;
  startTime: string;
  endTime: string;
  createdAt: string;
  updatedAt: string;
  ordinaryhours: OrdinaryHours;
  ordinaryhoursdecimal: number;
  earned: number;
  flexiTimeHours: number;
  currentDate: string;
  totalHours: number;
  weekDay: string;
  weekNumber: number;
};

type DayProps = {
  date: DayType;
  isExport?: boolean;
};

export const Day = ({ date, isExport = false }: DayProps) => {
  const { t } = useTranslation();
  const {
    day,
    lunchTime,
    overtimeType,
    isWorkingDay,
    overtimeHours,
    totalHours,
    ordinaryhoursdecimal,
    flexiTimeHours,
    id,
    description,
    earned,
  } = date;
  const isFlexiTime = overtimeType === 'flex';
  const startTime = stringToDaysJsFormat(date.startTime);
  const currentDate = new Date(date.currentDate);
  const endTime = stringToDaysJsFormat(date.endTime);

  const queryClient = useQueryClient();
  const timeFormat = 'HH:mm';
  const { i18n } = useTranslation();
  const handleChangeRegistration = (data = {}) =>
    updateDay({ ...data, id }, () => {
      queryClient.invalidateQueries({ queryKey: ['useRegistrations'] });
      queryClient.invalidateQueries({ queryKey: ['useYearStats'] });
    });

  const formatTimeFromDayJs = (dateAsDayJs: any) => {
    const dayjsObj = Array.isArray(dateAsDayJs) ? dateAsDayJs[0] : dateAsDayJs;
    return dayjsObj?.format('HH:mm') || '08:00';
  };

  const createTimeData = (timeType: 'startTime' | 'endTime', dateAsDayJs: any) => ({
    [timeType]: `${format(currentDate, "yyyy'-'MM'-'dd")} ${formatTimeFromDayJs(dateAsDayJs)}`,
  });

  if (isExport) {
    return (
      <div
        className={`${styles.dayWrapper} ${styles.dayWrapperExport}`}
        style={{
          color: !isWorkingDay ? 'red' : 'black',
          background: isBefore(sub(new Date(), { days: 1 }), currentDate) && !isExport ? 'white' : '#eee',
        }}
      >
        <div className={styles.date}>{day}</div>

        <div className={styles.weekday}>{formatWeekdayOnly(currentDate, i18n.language)}</div>

        <div className={styles.start}>
          <TimePicker
            disabledTime={disabledDateTime}
            minuteStep={30}
            format={timeFormat}
            defaultValue={startTime ? dayjs(startTime, 'HH:mm') : dayjs('08:00', 'HH:mm')}
            onCalendarChange={(dateAsDayJs) => handleChangeRegistration(createTimeData('startTime', dateAsDayJs))}
            hideDisabledOptions={true}
            allowClear={false}
            showNow={false}
          />
        </div>
        <div className={styles.stop}>
          <TimePicker
            disabledTime={disabledDateTime}
            minuteStep={30}
            format={timeFormat}
            defaultValue={endTime ? dayjs(endTime, 'HH:mm') : dayjs('16:00', 'HH:mm')}
            onCalendarChange={(dateAsDayJs) => handleChangeRegistration(createTimeData('endTime', dateAsDayJs))}
            hideDisabledOptions={true}
            allowClear={false}
            showNow={false}
          />
        </div>
        <Cascader
          options={breakOptions}
          value={lunchTime ? [lunchTime] : undefined}
          className={styles.lunch}
          onChange={(e) =>
            handleChangeRegistration({
              lunchTime: e[0],
            })
          }
          placeholder={t('salary.pleaseSelect')}
        />

        <div className={styles.total}>{totalHours}</div>
        <div className={styles.brutto}>{formatCurrency(earned)}</div>
      </div>
    );
  }

  return (
    <div
      className={`${styles.dayWrapper} ${isExport ? styles.dayWrapperExport : ''}`}
      style={{
        color: !isWorkingDay ? 'red' : 'black',
        ...(isToday(currentDate) && { border: '2px dotted lightgreen' }),
        background: isBefore(sub(new Date(), { days: 1 }), currentDate) && !isExport ? 'white' : '#eee',
      }}
    >
      <div className={styles.date}>{day}</div>
      <div className={styles.weekday}>{formatWeekdayOnly(currentDate, i18n.language)}</div>
      <div className={styles.workday}>
        <Checkbox
          checked={isWorkingDay}
          onChange={() => {
            handleChangeRegistration({
              isWorkingDay: !isWorkingDay,
            });
          }}
        />
      </div>

      <div className={styles.start}>
        <TimePicker
          disabledTime={disabledDateTime}
          minuteStep={30}
          format={timeFormat}
          defaultValue={startTime ? dayjs(startTime, 'HH:mm') : dayjs('08:00', 'HH:mm')}
          onCalendarChange={(dateAsDayJs) => handleChangeRegistration(createTimeData('startTime', dateAsDayJs))}
          hideDisabledOptions={true}
          allowClear={false}
          showNow={false}
        />
      </div>
      <div className={styles.stop}>
        <TimePicker
          disabledTime={disabledDateTime}
          minuteStep={30}
          format={timeFormat}
          defaultValue={endTime ? dayjs(endTime, 'HH:mm') : dayjs('16:00', 'HH:mm')}
          onCalendarChange={(dateAsDayJs) => handleChangeRegistration(createTimeData('endTime', dateAsDayJs))}
          hideDisabledOptions={true}
          allowClear={false}
          showNow={false}
        />
      </div>
      <Cascader
        options={breakOptions}
        value={lunchTime ? [lunchTime] : undefined}
        className={styles.lunch}
        onChange={(e) =>
          handleChangeRegistration({
            lunchTime: e[0],
          })
        }
        placeholder={t('salary.pleaseSelect')}
      />

      <div className={styles.flexTime}>
        <Checkbox
          checked={isFlexiTime}
          onChange={() => {
            handleChangeRegistration({
              overtimeType: isFlexiTime ? 'Paid' : 'flex',
            });
          }}
        />
      </div>

      <div className={styles.ordHours}>{ordinaryhoursdecimal}</div>
      <div className={styles.overtimeHours}>
        {overtimeType === 'flex' ? `${flexiTimeHours} (flex)` : `${overtimeHours}`}
      </div>
      <div className={styles.total}>{totalHours}</div>
      <div className={styles.brutto}>{formatCurrency(earned)}</div>
      {!isExport && (
        <div className={styles.description}>
          <RegistrationInputDebounced
            registrationId={id}
            label={t('salary.note')}
            propertyName="description"
            value={description}
            textArea
          />
        </div>
      )}
    </div>
  );
};
